/**
 * resolveCoverageYear — derive the claim's coverage year from saved data only.
 *
 * NEVER falls back silently to `new Date().getFullYear()`. That silent
 * fallback is the root cause of phantom "2026-04 $0 $0 $0" rows appearing
 * on the Summary / Reconciliation pages.
 *
 * Priority:
 *   1. Explicit data.coverage_year
 *   2. Year of event_start_date / loss_event_date / loss_start_date
 *   3. Max year in data.financial_data.yearly_data keys (data_YYYY)
 *   4. Max year in any Array<{year}> under financial_data (legacy shape)
 *   5. Max tax_year from tax_data[]
 *   6. → returns null (or throws, depending on options)
 *
 * Callers MUST handle null — don't silently substitute today's year.
 */
export interface ClaimLike {
  coverage_year?: number | string | null;
  event_start_date?: string | null;
  event_end_date?: string | null;
  loss_event_date?: string | null;
  loss_start_date?: string | null;
  financial_data?: unknown;
  tax_data?: Array<{ tax_year?: number | string | null }> | null;
  calculated_years?: number[] | null;
}

export interface ResolveOptions {
  /** "null" (default): return null when unresolved. "throw": throw Error. */
  onUnresolved?: "null" | "throw";
}

const yearFromDateString = (v: unknown): number | null => {
  if (typeof v !== "string" || v.length < 4) return null;
  const y = parseInt(v.slice(0, 4), 10);
  return Number.isFinite(y) && y >= 1970 && y <= 2100 ? y : null;
};

const yearsFromYearlyData = (fd: unknown): number[] => {
  if (!fd || typeof fd !== "object") return [];
  const years: number[] = [];
  const record = fd as Record<string, unknown>;

  // Shape A: { yearly_data: { "2020": [...], "2021": [...] } }
  const yearly = record.yearly_data;
  if (yearly && typeof yearly === "object") {
    for (const k of Object.keys(yearly)) {
      const n = parseInt(k, 10);
      if (Number.isFinite(n) && n >= 1970 && n <= 2100) years.push(n);
    }
  }

  // Shape B: { data_2020: [...], data_2021: [...] }
  for (const k of Object.keys(record)) {
    if (k.startsWith("data_")) {
      const n = parseInt(k.slice(5), 10);
      if (Number.isFinite(n) && n >= 1970 && n <= 2100) years.push(n);
    }
  }

  // Shape C: { revenue: [{year, month, amount}, ...] }
  const revenue = record.revenue;
  if (Array.isArray(revenue)) {
    for (const item of revenue) {
      const y = typeof item?.year === "number" ? item.year : parseInt(String(item?.year ?? ""), 10);
      if (Number.isFinite(y) && y >= 1970 && y <= 2100) years.push(y);
    }
  }

  return years;
};

/**
 * Resolve the coverage year from claim data. Returns null if indeterminable.
 * Never silently returns today's year.
 */
export function resolveCoverageYear(
  data: ClaimLike | null | undefined,
  options: ResolveOptions = {}
): number | null {
  const { onUnresolved = "null" } = options;

  const unresolved = (): number | null => {
    if (onUnresolved === "throw") {
      throw new Error(
        "resolveCoverageYear: could not derive coverage_year from claim data. " +
          "Data has no coverage_year, event date, financial yearly data, or tax years."
      );
    }
    return null;
  };

  if (!data) return unresolved();

  // 1. Explicit
  const cy = data.coverage_year;
  if (typeof cy === "number" && Number.isFinite(cy) && cy >= 1970 && cy <= 2100) {
    return cy;
  }
  if (typeof cy === "string") {
    const n = parseInt(cy, 10);
    if (Number.isFinite(n) && n >= 1970 && n <= 2100) return n;
  }

  // 2. Event / loss dates
  for (const key of ["event_start_date", "event_end_date", "loss_event_date", "loss_start_date"] as const) {
    const y = yearFromDateString(data[key]);
    if (y !== null) return y;
  }

  // 3. + 4. Financial data keys / revenue arrays
  const fdYears = yearsFromYearlyData(data.financial_data);
  if (fdYears.length > 0) return Math.max(...fdYears);

  // 5. calculated_years (legacy pre-computed list)
  if (Array.isArray(data.calculated_years) && data.calculated_years.length > 0) {
    const valid = data.calculated_years.filter(
      (y) => Number.isFinite(y) && y >= 1970 && y <= 2100
    );
    if (valid.length > 0) return Math.max(...valid);
  }

  // 6. tax_data
  if (Array.isArray(data.tax_data)) {
    const years: number[] = [];
    for (const t of data.tax_data) {
      // Writers have historically been inconsistent: sometimes tax_year is
      // set at the entry level, sometimes just `year`, sometimes only at
      // `data.tax_year` on the nested parser output.  Try all three.
      const candidates = [
        (t as any)?.tax_year,
        (t as any)?.year,
        (t as any)?.data?.tax_year,
        (t as any)?.data?.year,
      ];
      for (const raw of candidates) {
        const n = typeof raw === "number" ? raw : parseInt(String(raw ?? ""), 10);
        if (Number.isFinite(n) && n >= 1970 && n <= 2100) {
          years.push(n);
          break;
        }
      }
    }
    if (years.length > 0) return Math.max(...years);
  }

  return unresolved();
}

/**
 * Resolve a bucketing year for a financial data-point (revenue/expense row)
 * that lacks an explicit year. Returns null when the item has no year and
 * the claim has no coverage year.
 *
 * Use this instead of `item.year || new Date().getFullYear()`.
 *
 * If this returns null, the caller should DROP the row and log a warning —
 * silently bucketing unknown-year rows under today produces phantom
 * "2026-04 $0 $0 $0" rows on the Summary page.
 */
export function resolveBucketYear(
  item: { year?: number | string | null } | null | undefined,
  claim: ClaimLike | null | undefined
): number | null {
  const raw = item?.year;
  if (typeof raw === "number" && Number.isFinite(raw) && raw >= 1970 && raw <= 2100) {
    return raw;
  }
  if (typeof raw === "string") {
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 1970 && n <= 2100) return n;
  }
  return resolveCoverageYear(claim);
}
